import React from 'react'
import PropTypes from 'prop-types'
import { createComponent } from 'react-fela'

import { merge, getIn } from 'timm'
import unescape from 'lodash/unescape'
import { to12HrFormat, pick } from 'design-framework/utils/helpers'
import { inViewPort } from 'design-framework/utils/scroll-helper'
import Icon from 'design-framework/common/ui/Icon'
import CloseIcon from 'design-framework/common/icons/close-cross-white.svg'

import { __pills, __listItem, __closeIcon } from './__style'
import Carousel from '../Carousel/index'

const Pill = createComponent(__pills)
const ListItem = createComponent(__listItem, 'li', [
	'onClick',
	'data-id',
	'data-auto',
])

// if not wrapped in a stateless component, the export is not found.
// Probably issue with styleguidist or fela.

class Pills extends React.Component {
	constructor(props) {
		super(props)
		this.state = {
			list: props.list,
			timeStatusMap: props.timeStatusMap || {},
		}
		this.wrapper = React.createRef()
	}

	componentDidUpdate() {
		const { scrollIntoView } = this.props
		if (scrollIntoView) {
			if (getIn(this.wrapper, ['current', 'scrollIntoView'])) {
				const isElementVisible = inViewPort(this.wrapper.current)
				if (!isElementVisible) {
					this.wrapper.current.scrollIntoView()
				}
			}
		}
	}

	handleClick = pill => {
		const { list } = this.state
		const currentState = list[pill]
		if (this.props.allowMultiSelect) {
			if (!this.props.noLocalStateUpdateOnClick) {
				this.setState({
					list: { ...list, [pill]: !currentState },
				})
			}
			if (typeof this.props.onToggle === 'function')
				this.props.onToggle({
					title: this.props.title || '',
					slug: this.props.slug || '',
					label: pill,
					isActive: !currentState,
					list: { ...this.state.list, [pill]: !currentState },
				})
		} else if (this.props.noToggle) {
			// When toggle is disabled
			if (!currentState) {
				if (!this.props.noLocalStateUpdateOnClick) {
					const newList = {}
					Object.keys(this.state.list || {}).forEach(label => {
						newList[label] = label === pill ? !currentState : false
					})
					this.setState({
						list: newList,
					})
				}
				if (typeof this.props.onToggle === 'function')
					this.props.onToggle({
						title: this.props.title || '',
						slug: this.props.slug || '',
						label: pill,
						isActive: !currentState,
						list: pill,
					})
			}
		} else {
			if (!this.props.noLocalStateUpdateOnClick) {
				const newList = {}
				Object.keys(this.state.list || {}).forEach(label => {
					newList[label] = label === pill ? !currentState : false
				})
				this.setState({
					list: newList,
				})
			}
			if (typeof this.props.onToggle === 'function')
				this.props.onToggle({
					title: this.props.title || '',
					slug: this.props.slug || '',
					label: pill,
					isActive: !currentState,
					list: pill,
				})
		}
	}

	// eslint-disable-next-line camelcase
	UNSAFE_componentWillReceiveProps(props) {
		this.setState({
			list: props.list,
			timeStatusMap: props.timeStatusMap || {},
		})
	}

	renderPills = (
		list,
		disabled,
		clickable,
		convertTo12,
		displayLimit,
		customColor,
		isSquared,
		quickfilter,
		loading,
		noToggle,
		customStyle,
		isDesktop,
		subTextMargin,
		noFirstLastPillMargin,
		showCloseIconOnActive
	) => {
		const {
			customList,
			legend = {},
			availableSeats = {},
			styles,
		} = this.props
		const { timeStatusMap = {} } = this.state
		const pill = []
		let pillList = list
		const listKeys = customList || Object.keys(pillList) || []
		// Display elements based on the displayLimit
		if (displayLimit && listKeys.length > displayLimit) {
			const reducedList = pick(pillList, listKeys.slice(0, displayLimit))
			pillList = merge(reducedList, {
				[`+${listKeys.length - displayLimit}`]: true,
			})
		}
		listKeys.forEach(label => {
			if (Object.keys(disabled).length) {
				pill.push(
					<ListItem
						style={styles}
						key={label}
						isActive={this.state.list[label] || false}
						clickable={clickable}
						customColor={customColor}
						isSquared={isSquared}
						onClick={
							disabled[label] && clickable
								? () => this.handleClick(label)
								: () => {}
						}
						data-id="time-pill"
						data-auto="time-pill"
						disable={!disabled[label]}
						status={legend[timeStatusMap[label]] || ''}
						quickfilter={quickfilter}
						loading={loading}
						subText={`${availableSeats[label]} LEFT`}
						customStyle={customStyle}
						isDesktop={isDesktop}
						marginStyle={availableSeats[label] ? subTextMargin : 0}
					>
						{convertTo12 ? to12HrFormat(label) : unescape(label)}
					</ListItem>
				)
			} else {
				pill.push(
					<ListItem
						style={styles}
						key={label}
						isActive={this.state.list[label] || false}
						onClick={
							clickable ? () => this.handleClick(label) : () => {}
						}
						customColor={customColor}
						clickable={clickable}
						isSquared={isSquared}
						status={legend[timeStatusMap[label]] || ''}
						quickfilter={quickfilter}
						loading={loading}
						subText={`${availableSeats[label]} LEFT`}
						customStyle={customStyle}
						isDesktop={isDesktop}
						noFirstLastPillMargin={noFirstLastPillMargin}
					>
						{convertTo12 ? to12HrFormat(label) : unescape(label)}
						{showCloseIconOnActive && this.state.list[label] && (
							<Icon glyph={CloseIcon} style={__closeIcon()} />
						)}
					</ListItem>
				)
			}
		})

		return pill
	}

	render() {
		const {
			list,
			disabled,
			clickable,
			convertTo12,
			displayLimit,
			customColor,
			backgroundColor,
			scrollablePills,
			isSquared,
			quickfilter,
			loading,
			noToggle,
			customStyle,
			isDesktop,
			hasCarousel,
			hideScrollbar,
			additionalCarouselOptions,
			subTextMargin,
			noFirstLastPillMargin,
			showCloseIconOnActive,
		} = this.props

		const pills = this.renderPills(
			list,
			disabled,
			clickable,
			convertTo12,
			displayLimit,
			customColor,
			isSquared,
			quickfilter,
			loading,
			noToggle,
			customStyle,
			isDesktop,
			subTextMargin,
			noFirstLastPillMargin,
			showCloseIconOnActive
		)

		const showCarousel =
			hasCarousel && pills.length > additionalCarouselOptions.slidesToShow

		return (
			<Pill
				innerRef={this.wrapper}
				{...{ backgroundColor, scrollablePills }}
				scrollablePills={scrollablePills}
				hasCarousel={showCarousel}
				hideScrollbar={hideScrollbar}
				isDesktop={isDesktop}
				quickfilter={quickfilter}
			>
				{showCarousel ? (
					<Carousel
						additionalCarouselOptions={additionalCarouselOptions}
					>
						{pills}
					</Carousel>
				) : (
					<ul>{pills}</ul>
				)}
			</Pill>
		)
	}
}

Pills.propTypes = {
	/** Sets the background color of the pills */
	backgroundColor: PropTypes.string,
	/** Sets the font weight of the pills */
	fontWeight: PropTypes.string,
	/** Sets the font size of the pills */
	fontSize: PropTypes.string,
	/** Sets the padding top of the pills */
	paddingTop: PropTypes.string,
	/** Sets the padding bottom of the pills */
	paddingBottom: PropTypes.string,
	/** Sets the padding left of the pills */
	paddingLeft: PropTypes.string,
	/** Sets the padding right of the pills */
	paddingRight: PropTypes.string,
	/** Sets the text style of the pills */
	textTransform: PropTypes.string,
	/** Keys: Labels of pills, Values: is pill active? */
	list: PropTypes.objectOf(PropTypes.bool),
	/** Keys: Labels of pills, Values: is pill disabled */
	disabled: PropTypes.object,
	/** Sets the padding right of the pills */
	marginRight: PropTypes.string,
	/** render list of pills */
	renderPills: PropTypes.func,
	/** Callback called when a pill is clicked. Param to callback: Pill text */
	onToggle: PropTypes.func,
	/** Can multiple pills be selected */
	allowMultiSelect: PropTypes.bool,
	/** Should be clickable or not */
	clickable: PropTypes.bool,
	/** A title to bind the set of pills to. Will be passed in the callback */
	title: PropTypes.string,
	/** We need the slug for toggle in Filters.js page. Will be set as key value which will hold the clicked pill data */
	slug: PropTypes.string,
	/** To enable scroll for pills wrapper */
	scrollablePills: PropTypes.bool,
	/** To convert label to time format */
	convertTo12: PropTypes.bool,
	/** Custom Color */
	customColor: PropTypes.string,
	/** Custom sorted list */
	customList: PropTypes.array,
	/** Custom Style */
	customStyle: PropTypes.bool,
	timeStatusMap: PropTypes.object,
	legend: PropTypes.object,
	isDesktop: PropTypes.bool,
	scrollIntoView: PropTypes.bool,
	/** this is use to remove the extra margin from first and last child of Pills */
	noFirstLastPillMargin: PropTypes.bool,
	/** this is use to show the Close Icon on active in Pill */
	showCloseIconOnActive: PropTypes.bool,
	/** disable local state update of list on click */
	noLocalStateUpdateOnClick: PropTypes.bool,
	hideScrollbar: PropTypes.bool,
	/** style for pills */
	styles: PropTypes.object,
}

Pills.defaultProps = {
	allowMultiSelect: false,
	clickable: true,
	scrollablePills: false,
	convertTo12: false,
	disabled: {},
	customColor: '',
	timeStatusMap: {},
	legend: {},
	customStyle: false,
	scrollIntoView: false,
	noFirstLastPillMargin: false,
	showCloseIconOnActive: false,
	noLocalStateUpdateOnClick: false,
	hideScrollbar: false,
}

export default Pills
